<template>
  <div class="label-sidebar">
    <LabelFilterSidebarHeader
      ref="header"
      :searching="searching"
      :keyword="keyword"
      @search="onKeywordChange"
      @open-search="openSearch"
      @close-search="closeSearch"
      @close="close"
    />

    <div class="text-center text-grey-6 text-caption q-px-md q-pt-xs">
      AI-generated content may be incorrect.
    </div>

    <SelectedLabelChips
      v-if="hasSelection"
      :labels="selectedLabels"
      :categories="selectedCategories"
      @remove="toggle"
      @remove-category="toggleCategory"
      @clear="clearSelection"
    />

    <div class="label-sidebar__scroll-wrapper">
      <div
        v-if="isLoading && !labels.length"
        class="row justify-center q-pa-md"
      >
        <q-spinner size="sm" />
      </div>

      <div
        v-else-if="!labels.length"
        class="text-grey text-center q-pa-md"
      >
        {{ keyword ? 'No matches' : 'No insights available' }}
      </div>

      <template v-else-if="groupedByCategory.length">
        <LabelCategoryGroup
          v-for="group in groupedByCategory"
          :key="group.name || '__uncategorized'"
          :group="group"
          :selection="selection"
          @toggle="toggle"
          @toggle-category="toggleCategory"
        />
      </template>

      <InfiniteScroll
        :active="hasMore"
        :loading="loadingMore"
        @load-more="loadMore"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import _ from 'lodash';
import { LabelSelection, LabelSummary } from '@/analysis/types';
import InfiniteScroll from '@/components/InfiniteScroll.vue';
import { HasTaskMixin } from '@/mixins';
import labelRepository from './label-repository';
import LabelCategoryGroup from './LabelCategoryGroup.vue';
import LabelFilterSidebarHeader from './LabelFilterSidebarHeader.vue';
import SelectedLabelChips from './SelectedLabelChips.vue';
import {
  emptySelection, isEmpty, toggleCategory, toggleLabel,
} from './selection';

const PAGE_SIZE = 100;

interface CategoryGroup {
  id: number;
  name: string;
  count: number;
  labels: LabelSummary[];
}

interface CategorySummary {
  id: number;
  name: string;
}

interface Data {
  loadingMore: boolean;
  labels: LabelSummary[];
  total: number;
  offset: number;
  selection: LabelSelection;
  searching: boolean;
  keyword: string;
}

export default defineComponent({
  emits: ['close', 'update:value'],
  mixins: [HasTaskMixin],
  components: {
    LabelCategoryGroup,
    LabelFilterSidebarHeader,
    InfiniteScroll,
    SelectedLabelChips,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    value: {
      type: Object as () => LabelSelection,
      default: () => emptySelection(),
    },
  },
  data(): Data {
    return {
      loadingMore: false as boolean,
      labels: [],
      total: 0,
      offset: 0,
      selection: { ...emptySelection(), ...this.value },
      searching: false,
      keyword: '',
    };
  },
  computed: {
    groupedByCategory(): CategoryGroup[] {
      const groups: Record<string, CategoryGroup> = {};

      this.labels.forEach((label) => {
        const key = label.categoryName || '';
        if (!groups[key]) {
          groups[key] = {
            id: label.categoryId,
            name: key,
            count: label.categoryCount,
            labels: [],
          };
        }
        groups[key].labels.push(label);
      });

      const named = Object.values(groups)
        .filter(g => g.name)
        .sort((a, b) => b.count - a.count);

      const uncategorized = groups[''];
      if (uncategorized) {
        named.push(uncategorized);
      }

      return named;
    },

    selectedLabels(): LabelSummary[] {
      return this.selection.labelIds
        .map(id => this.labels.find(l => l.id === id))
        .filter((l): l is LabelSummary => !!l);
    },

    selectedCategories(): CategorySummary[] {
      return this.selection.labelCategoryIds
        .map(id => this.labels.find(l => l.categoryId === id))
        .filter((l): l is LabelSummary => !!l)
        .map(label => ({ id: label.categoryId, name: label.categoryName }));
    },

    hasSelection(): boolean {
      return !isEmpty(this.selection);
    },

    hasMore(): boolean {
      return this.labels.length < this.total;
    },
  },
  watch: {
    keyword: _.debounce(function dLoad(this: any) {
      this.load();
    }, 300),
  },
  created() {
    this.load();
  },
  methods: {
    close() {
      this.$emit('close');
    },

    openSearch() {
      this.searching = true;
      this.$nextTick(() => {
        (this.$refs.header as any).focus();
      });
    },

    closeSearch() {
      this.searching = false;
      this.keyword = '';
    },

    onKeywordChange(kw: string) {
      this.keyword = kw;
    },

    load() {
      this.task = labelRepository.findAll(this.clientId, {
        keyword: this.keyword || undefined,
        offset: 0,
        limit: PAGE_SIZE,
      });

      this.task.then((result) => {
        this.labels = result.items;
        this.total = result.total;
        this.offset = PAGE_SIZE;
      });
    },

    async loadMore() {
      if (this.loadingMore) return;
      this.loadingMore = true;
      try {
        const result = await labelRepository.findAll(this.clientId, {
          keyword: this.keyword || undefined,
          offset: this.offset,
          limit: PAGE_SIZE,
        });
        this.labels.push(...result.items);
        this.offset += PAGE_SIZE;
      } finally {
        this.loadingMore = false;
      }
    },

    toggle(label: LabelSummary) {
      this.select(toggleLabel(this.selection, label));
    },

    toggleCategory(categoryId: number) {
      const categoryLabelIds = this.labels
        .filter(label => label.categoryId === categoryId)
        .map(label => label.id);

      this.select(toggleCategory(this.selection, categoryId, categoryLabelIds));
    },

    clearSelection() {
      this.select(emptySelection());
    },

    select(selection: LabelSelection) {
      this.selection = selection;
      this.$emit('update:value', selection);
    },
  },
});
</script>

<style scoped>
.label-sidebar {
  /* width: 360px; */
  background: white;
  display: flex;
  flex-direction: column;
}

.label-sidebar__scroll-wrapper {
  overflow-y: auto;
  max-height: calc(100vh - 100px);
  flex-shrink: 1;
}
</style>
